"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "OauthTokenService", {
    enumerable: true,
    get: function() {
        return OauthTokenService;
    }
});
const _crypto = require("crypto");
const _util = require("util");
const _common = require("@nestjs/common");
const _bcryptjs = /*#__PURE__*/ _interop_require_default(require("bcryptjs"));
const _jsonwebtoken = /*#__PURE__*/ _interop_require_wildcard(require("jsonwebtoken"));
const _models = require("~/models");
const _ncError = require("~/helpers/ncError");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _getRequireWildcardCache(nodeInterop) {
    if (typeof WeakMap !== "function") return null;
    var cacheBabelInterop = new WeakMap();
    var cacheNodeInterop = new WeakMap();
    return (_getRequireWildcardCache = function(nodeInterop) {
        return nodeInterop ? cacheNodeInterop : cacheBabelInterop;
    })(nodeInterop);
}
function _interop_require_wildcard(obj, nodeInterop) {
    if (!nodeInterop && obj && obj.__esModule) {
        return obj;
    }
    if (obj === null || typeof obj !== "object" && typeof obj !== "function") {
        return {
            default: obj
        };
    }
    var cache = _getRequireWildcardCache(nodeInterop);
    if (cache && cache.has(obj)) {
        return cache.get(obj);
    }
    var newObj = {
        __proto__: null
    };
    var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor;
    for(var key in obj){
        if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) {
            var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null;
            if (desc && (desc.get || desc.set)) {
                Object.defineProperty(newObj, key, desc);
            } else {
                newObj[key] = obj[key];
            }
        }
    }
    newObj.default = obj;
    if (cache) {
        cache.set(obj, newObj);
    }
    return newObj;
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
let OauthTokenService = class OauthTokenService {
    validatePKCE(params) {
        const { codeVerifier, codeChallenge, codeChallengeMethod } = params;
        if (!codeChallenge || !codeVerifier) {
            return false;
        }
        if (codeChallengeMethod !== 'S256') {
            return false;
        }
        if (codeVerifier.length < 43 || codeVerifier.length > 128) {
            return false;
        }
        const allowedChars = /^[a-zA-Z0-9._~-]+$/;
        if (!allowedChars.test(codeVerifier)) {
            return false;
        }
        const computedChallenge = (0, _crypto.createHash)('sha256').update(codeVerifier).digest('base64url');
        return computedChallenge === codeChallenge;
    }
    async generateAccessToken(payload) {
        const user = await _models.User.get(payload.userId);
        const now = Math.floor(Date.now() / 1000);
        return _jsonwebtoken.sign({
            sub: payload.userId,
            email: user.email,
            client_id: payload.clientId,
            scope: payload.scope,
            iat: now,
            exp: now + this.ACCESS_TOKEN_EXPIRES_IN,
            id: user.id,
            roles: user.roles,
            token_version: user.token_version
        }, _Noco.default.config.auth.jwt.secret, {
            algorithm: 'HS256'
        });
    }
    async authenticateClient(params) {
        const { clientId, clientSecret, isPKCEFlow = false } = params;
        const client = await _models.OAuthClient.getByClientId(clientId);
        if (!client) {
            throw new Error('invalid_client: Client not found');
        }
        // Confidential clients: require client_secret OR valid PKCE
        if (client.client_secret) {
            // If PKCE is used, client_secret is optional
            if (isPKCEFlow) {
                // If client_secret is provided with PKCE, validate it using bcrypt
                if (clientSecret) {
                    const isValidSecret = await (0, _util.promisify)(_bcryptjs.default.compare)(clientSecret, client.client_secret);
                    if (!isValidSecret) {
                        throw new Error('invalid_client: Invalid client credentials');
                    }
                }
            // PKCE validation will happen separately, so we're good here
            } else {
                // Non-PKCE flow: client_secret is required
                if (!clientSecret) {
                    throw new Error('invalid_client: Client secret required for confidential clients without PKCE');
                }
                // Validate client_secret using bcrypt
                const isValidSecret = await (0, _util.promisify)(_bcryptjs.default.compare)(clientSecret, client.client_secret);
                if (!isValidSecret) {
                    throw new Error('invalid_client: Invalid client credentials');
                }
            }
        } else {
            // Public clients (no client_secret)
            // PKCE is required for public clients, but they don't need a secret
            if (clientSecret) {
                throw new Error('invalid_client: Client secret not expected for public clients');
            }
        }
        return client;
    }
    async exchangeCodeForTokens(params) {
        const { code, redirectUri, codeVerifier, clientSecret } = params;
        // Get authorization code
        const authCode = await _models.OAuthAuthorizationCode.getByCode(code);
        if (!authCode) {
            throw new Error('invalid_grant: Invalid or expired authorization code');
        }
        if (params.resource && authCode.resource && params.resource !== authCode.resource) {
            throw new Error('invalid_grant: resource parameter does not match authorized resource');
        }
        // Check if code is already used
        if (authCode.is_used) {
            throw new Error('invalid_grant: Authorization code has already been used');
        }
        // Check if code is expired
        if (new Date(authCode.expires_at) < new Date()) {
            throw new Error('invalid_grant: Authorization code has expired');
        }
        // Validate redirect URI
        if (authCode.redirect_uri !== redirectUri) {
            throw new Error('invalid_grant: Invalid redirect_uri');
        }
        // Determine if this is a PKCE flow
        const isPKCEFlow = !!authCode.code_challenge;
        // Validate PKCE if code challenge was provided during authorization
        if (isPKCEFlow) {
            if (!codeVerifier) {
                throw new Error('invalid_request: code_verifier is required for PKCE flow');
            }
            const isValidPKCE = this.validatePKCE({
                codeVerifier,
                codeChallenge: authCode.code_challenge,
                codeChallengeMethod: authCode.code_challenge_method
            });
            if (!isValidPKCE) {
                throw new Error('invalid_grant: Invalid code_verifier');
            }
        }
        // Authenticate client - PKCE makes client_secret optional for confidential clients
        await this.authenticateClient({
            clientId: authCode.fk_client_id,
            clientSecret,
            isPKCEFlow
        });
        const now = Date.now();
        const accessTokenExpiresAt = new Date(now + this.ACCESS_TOKEN_EXPIRES_IN * 1000).toISOString();
        const refreshTokenExpiresAt = new Date(now + this.REFRESH_TOKEN_EXPIRES_IN * 1000).toISOString();
        // Generate tokens
        const accessToken = await this.generateAccessToken({
            userId: authCode.fk_user_id,
            clientId: authCode.fk_client_id,
            scope: authCode.scope
        });
        const refreshToken = (0, _crypto.randomBytes)(64).toString('base64url');
        const insertObj = {
            fk_client_id: authCode.fk_client_id,
            fk_user_id: authCode.fk_user_id,
            access_token: accessToken,
            access_token_expires_at: accessTokenExpiresAt,
            refresh_token: refreshToken,
            refresh_token_expires_at: refreshTokenExpiresAt,
            scope: authCode.scope,
            granted_resources: authCode.granted_resources,
            resource: authCode.resource
        };
        await _models.OAuthToken.insert(insertObj);
        // Mark authorization code as used
        await _models.OAuthAuthorizationCode.markAsUsed(code);
        return {
            access_token: accessToken,
            token_type: 'Bearer',
            expires_in: this.ACCESS_TOKEN_EXPIRES_IN,
            refresh_token: refreshToken,
            refresh_expires_in: this.REFRESH_TOKEN_EXPIRES_IN,
            scope: 'mcp',
            resource: authCode.resource
        };
    }
    async refreshAccessToken(params) {
        const { refreshToken, clientId, clientSecret } = params;
        // Get token by refresh token
        const tokenRecord = await _models.OAuthToken.getByRefreshToken(refreshToken);
        if (!tokenRecord) {
            _ncError.NcError.badRequest('Invalid refresh token');
        }
        // Check if token is revoked
        if (tokenRecord.is_revoked) {
            _ncError.NcError.badRequest('Refresh token has been revoked');
        }
        // Check if refresh token is expired
        if (tokenRecord.refresh_token_expires_at && new Date(tokenRecord.refresh_token_expires_at) < new Date()) {
            _ncError.NcError.badRequest('Refresh token has expired');
        }
        // For refresh token flow, require client authentication
        // Note: Refresh tokens don't use PKCE, so client_secret is required for confidential clients
        await this.authenticateClient({
            clientId,
            clientSecret,
            isPKCEFlow: false
        });
        // Validate client ID
        if (tokenRecord.fk_client_id !== clientId) {
            _ncError.NcError.badRequest('Invalid client_id');
        }
        const now = Date.now();
        const newAccessTokenExpiresAt = new Date(now + this.ACCESS_TOKEN_EXPIRES_IN * 1000).toISOString();
        const newRefreshTokenExpiresAt = new Date(now + this.REFRESH_TOKEN_EXPIRES_IN * 1000).toISOString();
        // Generate new access token
        const newAccessToken = await this.generateAccessToken({
            userId: tokenRecord.fk_user_id,
            clientId: tokenRecord.fk_client_id,
            scope: tokenRecord.scope
        });
        // Rotate refresh tokens for security
        const newRefreshToken = (0, _crypto.randomBytes)(64).toString('base64url');
        // Revoke old token
        await _models.OAuthToken.revoke(tokenRecord.id);
        // Create new token record
        await _models.OAuthToken.insert({
            fk_client_id: tokenRecord.fk_client_id,
            fk_user_id: tokenRecord.fk_user_id,
            access_token: newAccessToken,
            access_token_expires_at: newAccessTokenExpiresAt,
            refresh_token: newRefreshToken,
            refresh_token_expires_at: newRefreshTokenExpiresAt,
            scope: tokenRecord.scope,
            granted_resources: tokenRecord.granted_resources,
            resource: tokenRecord.resource
        });
        return {
            access_token: newAccessToken,
            token_type: 'Bearer',
            expires_in: this.ACCESS_TOKEN_EXPIRES_IN,
            refresh_token: newRefreshToken,
            refresh_expires_in: this.REFRESH_TOKEN_EXPIRES_IN,
            scope: tokenRecord.scope
        };
    }
    async revokeToken(params) {
        const { token, clientId, clientSecret, tokenTypeHint } = params;
        // For token revocation, require client authentication
        // Note: Revocation doesn't use PKCE, so client_secret is required for confidential clients
        await this.authenticateClient({
            clientId,
            clientSecret,
            isPKCEFlow: false
        });
        let tokenRecord = null;
        // Try to find token based on hint or try both types
        if (tokenTypeHint === 'refresh_token') {
            tokenRecord = await _models.OAuthToken.getByRefreshToken(token);
        } else {
            // Try access token first, then refresh token
            tokenRecord = await _models.OAuthToken.getByAccessToken(token);
            if (!tokenRecord) {
                tokenRecord = await _models.OAuthToken.getByRefreshToken(token);
            }
        }
        if (!tokenRecord) {
            // Return success even if token doesn't exist
            return true;
        }
        // Validate client ID
        if (tokenRecord.fk_client_id !== clientId) {
            _ncError.NcError.badRequest('Invalid client_id');
        }
        // Revoke the token
        await _models.OAuthToken.revoke(tokenRecord.id);
        return true;
    }
    async listUserAuthorizations(userId) {
        const tokens = await _models.OAuthToken.listByUser(userId);
        const authorizationsList = [];
        for (const token of tokens){
            const client = await _models.OAuthClient.getByClientId(token.fk_client_id);
            if (client) {
                authorizationsList.push({
                    id: token.id,
                    client_id: client.client_id,
                    client_name: client.client_name,
                    client_description: client.client_description,
                    client_uri: client.client_uri,
                    logo_uri: client.logo_uri,
                    scope: token.scope,
                    granted_resources: token.granted_resources,
                    created_at: token.created_at,
                    last_used_at: token.last_used_at
                });
            }
        }
        return authorizationsList;
    }
    async revokeUserAuthorization(userId, tokenId) {
        const token = await _models.OAuthToken.get(tokenId);
        if (!token) {
            _ncError.NcError.notFound('OAuth authorization not found');
        }
        if (token.fk_user_id !== userId) {
            _ncError.NcError.forbidden('You do not have permission to revoke this authorization');
        }
        // Revoke the token
        await _models.OAuthToken.revoke(tokenId);
        return true;
    }
    constructor(){
        this.ACCESS_TOKEN_EXPIRES_IN = 60 * 60; // 1 hour in seconds
        this.REFRESH_TOKEN_EXPIRES_IN = 60 * 24 * 60 * 60; // 60 days in seconds
    }
};
OauthTokenService = _ts_decorate([
    (0, _common.Injectable)()
], OauthTokenService);
